import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { studentService, StudentWithStats } from '../../services/studentService';
import { ArtworkObjectImage } from '../artwork/ArtworkObjectImage';

const Section = styled.section`
  background: ${({ theme }) => theme.colors.gallery.surface};
  padding: clamp(3rem, 8vw, 6rem) 0;
  border-top: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 768px) {
    padding: 0 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.gallery.section};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  margin: 0;

  @media (min-width: 768px) {
    font-size: 28px;
  }
`;

const CountPill = styled.span`
  background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
  color: ${({ theme }) => theme.colors.gallery.text.muted};
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
`;

const SeeAll = styled(Link)`
  margin-left: auto;
  color: ${({ theme }) => theme.colors.gallery.accent.primary};
  font-size: 0.875rem;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.gallery.accent.hover};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 2px;
  }
`;

const MobileScrollTrack = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileCard = styled(Link)`
  flex: 0 0 110px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  cursor: pointer;
  padding: 0.75rem 0.5rem;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
  transition: background 150ms ease;

  &:hover {
    background: ${({ theme }) => theme.colors.gallery.surfaceElevated};
  }
`;

const Avatar = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.gallery.surfaceElevated};
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const Initials = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gallery.surfaceElevated};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.heading};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gallery.text.muted};
  font-size: ${({ $size }) => Math.round($size * 0.38)}px;
  flex-shrink: 0;
`;

const MobileName = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  text-align: center;
  line-height: 1.2;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MobileCount = styled.span`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.gallery.text.muted};
`;

const DesktopRow = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
`;

const DesktopChip = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.75rem 0.35rem 0.35rem;
  border-radius: 9999px;
  background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
  text-decoration: none;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease;

  &:hover {
    background: ${({ theme }) => theme.colors.gallery.surfaceElevated};
    border-color: ${({ theme }) => theme.colors.gallery.border.default};
  }
`;

const ChipName = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  white-space: nowrap;
`;

const ChipCount = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.gallery.text.muted};
  white-space: nowrap;
`;

const shimmerBase = `
  background: linear-gradient(90deg, ${theme.colors.semantic.surface} 25%, ${theme.colors.semantic.surfaceRaised} 50%, ${theme.colors.semantic.surface} 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  @keyframes shimmer {
    from { background-position: 200% 0; }
    to { background-position: -200% 0; }
  }
`;

const MobileSkeletonCard = styled.div`
  ${shimmerBase}
  flex: 0 0 110px;
  height: 120px;
  border-radius: 12px;
`;

const DesktopSkeletonChip = styled.div`
  ${shimmerBase}
  height: 34px;
  width: 120px;
  border-radius: 9999px;
`;

const SearchForm = styled.form`
  display: flex;
  gap: 0.5rem;
  margin: 0 0 1.25rem;

  @media (max-width: 480px) {
    display: grid;
  }
`;

const SearchInput = styled.input`
  min-width: 0;
  min-height: 44px;
  flex: 1;
  padding: 0 0.8rem;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font: inherit;

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 2px;
  }
`;

const SearchButton = styled.button`
  min-height: 44px;
  padding: 0 1rem;
  border: 1px solid ${({ theme }) => theme.colors.gallery.accent.border};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: ${({ theme }) => theme.colors.gallery.accent.primary};
  color: ${({ theme }) => theme.colors.gallery.text.inverse};
  font: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 2px;
  }
`;

const SearchEntrySection = styled.section`
  background: ${({ theme }) => theme.colors.gallery.surface};
  padding: 1rem 0 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
`;

const SearchEntryInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 768px) {
    padding: 0 2rem;
  }
`;

const SearchEntryTitle = styled.h2`
  margin: 0 0 0.5rem;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.heading};
  font-size: 1.15rem;
  font-weight: 600;
`;

const StateMessage = styled.div`
  display: grid;
  gap: 0.75rem;
  min-height: 80px;
  place-items: center;
  padding: 1rem;
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  text-align: center;
`;

const RetryButton = styled.button`
  min-height: 44px;
  padding: 0 0.9rem;
  border: 1px solid ${({ theme }) => theme.colors.semantic.borderStrong};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: ${({ theme }) => theme.colors.semantic.surfaceRaised};
  color: ${({ theme }) => theme.colors.semantic.textPrimary};
  font: inherit;
  cursor: pointer;
`;

function getInitials(student: StudentWithStats): string {
  return student.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase() || '?';
}

function getFullName(student: StudentWithStats): string {
  return student.name;
}

function AvatarOrInitials({
  student,
  size,
}: {
  student: StudentWithStats;
  size: number;
}) {
  const latestArtwork = student.latest_artworks?.[0];
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [latestArtwork?.artwork_id, latestArtwork?.updated_at]);

  if (latestArtwork && !imageFailed) {
    return (
      <Avatar $size={size}>
        <ArtworkObjectImage
          artworkId={latestArtwork.artwork_id}
          variant="thumbnail"
          updatedAt={latestArtwork.updated_at}
          alt=""
          onLoadError={() => setImageFailed(true)}
          onError={() => {
            setImageFailed(true);
          }}
        />
      </Avatar>
    );
  }

  return <Initials $size={size}>{getInitials(student)}</Initials>;
}

export function ArtistSearchEntry() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const queryString = search.trim();
    navigate(queryString ? `/artists?q=${encodeURIComponent(queryString)}` : '/artists');
  };

  return (
    <SearchEntrySection>
      <SearchEntryInner>
        <SearchEntryTitle>Find an artist</SearchEntryTitle>
        <SearchForm onSubmit={submitSearch}>
          <SearchInput type="search" aria-label="Search artists" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Find your artist" />
          <SearchButton type="submit">Search</SearchButton>
        </SearchForm>
      </SearchEntryInner>
    </SearchEntrySection>
  );
}

export function ArtistsStrip() {
  const query = useQuery({
    queryKey: ['artists-strip'],
    queryFn: () => studentService.getVisibleStudents(1, 20),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: 'always',
    retry: false,
  });

  const publicDataReady = query.isFetchedAfterMount && query.isSuccess && !query.isError && !query.isRefetching && query.fetchStatus !== 'paused';
  const students: StudentWithStats[] = publicDataReady ? (query.data?.students || []) : [];
  const total = students.length;

  if (!publicDataReady && (query.isFetching || query.fetchStatus === 'paused')) {
    const skeletons = Array.from({ length: 8 }, (_, i) => i);
    return (
      <Section>
        <Inner>
          <Header>
            <Title>Our Artists</Title>
            <SeeAll to="/artists">All artists →</SeeAll>
          </Header>
          <MobileScrollTrack>
            {skeletons.map((i) => <MobileSkeletonCard key={i} />)}
          </MobileScrollTrack>
          <DesktopRow>
            {skeletons.map((i) => <DesktopSkeletonChip key={i} />)}
          </DesktopRow>
        </Inner>
      </Section>
    );
  }

  if (query.isError && !query.isFetching) {
    return (
      <Section>
        <Inner>
          <Header><Title>Our Artists</Title><SeeAll to="/artists">All artists →</SeeAll></Header>
          <StateMessage role="alert">
            <span>Artists are temporarily unavailable.</span>
            <RetryButton type="button" onClick={() => { void query.refetch(); }} disabled={query.isFetching}>
              {query.isFetching ? 'Trying again…' : 'Try again'}
            </RetryButton>
          </StateMessage>
        </Inner>
      </Section>
    );
  }

  return (
    <Section>
      <Inner>
        <Header>
          <Title>Our Artists</Title>
          {total > 0 && <CountPill>{total}</CountPill>}
          <SeeAll to="/artists">All artists →</SeeAll>
        </Header>
        {students.length === 0 && <StateMessage role="status">No artist recommendations are available right now.</StateMessage>}

        <MobileScrollTrack>
          {students.map((student) => (
            <MobileCard
              key={student.student_id}
              to={`/artists/${student.student_id}`}
            >
              <AvatarOrInitials student={student} size={52} />
              <MobileName>{getFullName(student)}</MobileName>
              <MobileCount>{student.artwork_count} {student.artwork_count === 1 ? 'work' : 'works'}</MobileCount>
            </MobileCard>
          ))}
        </MobileScrollTrack>

        <DesktopRow>
          {students.map((student) => (
            <DesktopChip
              key={student.student_id}
              to={`/artists/${student.student_id}`}
            >
              <AvatarOrInitials student={student} size={34} />
              <ChipName>{getFullName(student)}</ChipName>
              <ChipCount>{student.artwork_count} {student.artwork_count === 1 ? 'work' : 'works'}</ChipCount>
            </DesktopChip>
          ))}
        </DesktopRow>
      </Inner>
    </Section>
  );
}
